import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};

export default function DealComparisonPage({ results, analysis }) {
  const dc = results?.deal_comparison || {};
  const seller = dc.seller;
  const buyer = dc.buyer;
  const comp = dc.comparable;
  const delta = dc.delta;

  const rows = [
    { label: 'Purchase Price', key: 'purchase_price', format: fmt },
    { label: 'Est. Working Capital', key: 'working_capital', format: fmt },
    { label: 'Closing Fees', key: 'closing_fees', format: fmt },
    { label: 'Total Deal Size', key: 'total_deal_size', format: fmt, highlight: true },
    { label: 'SDE / Adjusted EBITDA', key: 'sde', format: fmt },
    { label: 'SDE Multiple', key: 'sde_multiple', format: (v) => v?.toFixed(4) + 'x' },
    { label: 'Total Debt Service', key: 'total_debt_service', format: fmt },
    { label: 'DSCR', key: 'dscr', format: (v) => v?.toFixed(4) + 'x', badge: true },
    { label: 'Free Cash Flow to Buyer', key: 'fcf_to_buyer', format: fmt, highlight: true },
  ];

  const structureRows = [
    { label: 'SBA 7(a) Loan', key: 'sba_loan', format: fmt },
    { label: 'SBA Rate / Years', key: null, format: (s) => `${(s?.sba_rate * 100).toFixed(1)}% / ${s?.sba_years}yr` },
    { label: 'Seller Note', key: 'seller_note', format: fmt },
    { label: 'Seller Note Rate / Years', key: null, format: (s) => `${(s?.seller_note_rate * 100).toFixed(1)}% / ${s?.seller_note_years}yr` },
    { label: 'Equity Injection', key: 'equity_injection', format: fmt },
  ];

  if (!seller && !buyer) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="deal-comparison-page">
        Import data to view deal comparison
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="deal-comparison-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Deal Comparison</h2>
          <p className="text-sm text-zinc-400 mt-1">Seller vs Buyer vs Comparable — side by side analysis</p>
        </div>
        <SectionPdfExport
          title="Deal Comparison"
          analysisName={analysis?.name}
          rows={rows.map(r => ({ label: r.label, seller: r.format(seller?.[r.key]), buyer: r.format(buyer?.[r.key]), comp: r.format(comp?.[r.key]) }))}
          columns={buildColumns([
            { label: 'Metric', key: 'label' },
            { label: 'Seller', key: 'seller' },
            { label: 'Buyer', key: 'buyer' },
            { label: 'Comparable', key: 'comp' },
          ])}
        />
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium w-48">Metric</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Seller</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Buyer</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Comparable</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Delta (B vs S)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const sv = seller?.[row.key];
                const bv = buyer?.[row.key];
                const cv = comp?.[row.key];
                const dv = delta?.[row.key];

                return (
                  <TableRow key={row.label} className={`border-zinc-800/40 hover:bg-zinc-800/20 ${row.highlight ? 'bg-zinc-800/10' : ''}`}>
                    <TableCell className="text-xs text-zinc-300 font-medium">{row.label}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-200">
                      {row.format(sv)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-200">
                      {row.format(bv)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-200">
                      {row.format(cv)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.badge && dv !== undefined ? (
                        <Badge variant="outline" className={dv >= 0 ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}>
                          {dv >= 0 ? '+' : ''}{row.format(dv)}
                        </Badge>
                      ) : dv !== undefined ? (
                        <span className={dv > 0 ? 'text-emerald-400' : dv < 0 ? 'text-red-400' : 'text-zinc-500'}>
                          <DeltaIcon val={dv} />
                          {row.format ? row.format(Math.abs(dv)) : fmt(Math.abs(dv))}
                        </span>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Deal Structure */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Deal Structure</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium w-48">Component</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Seller</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Buyer</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Comparable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {structureRows.map((row, i) => (
                <TableRow key={i} className="border-zinc-800/40 hover:bg-zinc-800/20">
                  <TableCell className="text-xs text-zinc-300 font-medium">{row.label}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-zinc-200">
                    {row.key ? row.format(seller?.[row.key]) : row.format(seller)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-zinc-200">
                    {row.key ? row.format(buyer?.[row.key]) : row.format(buyer)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-zinc-200">
                    {row.key ? row.format(comp?.[row.key]) : row.format(comp)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Interpretation */}
      {buyer && seller && (
        <Card className="bg-zinc-900/60 border-indigo-500/20">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-400 space-y-1">
              <p><span className="text-indigo-400 font-medium">Buyer's advantage:</span> Lower total deal ({fmt(buyer.total_deal_size)} vs {fmt(seller.total_deal_size)}), lower debt service ({fmt(buyer.total_debt_service)} vs {fmt(seller.total_debt_service)}), higher DSCR ({buyer.dscr?.toFixed(2)}x vs {seller.dscr?.toFixed(2)}x).</p>
              {buyer.dscr >= 1.35 && <p className="text-emerald-400">DSCR exceeds SBA 1.35x threshold — deal is serviceable.</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DeltaIcon({ val }) {
  if (val > 0) return <ArrowUp className="w-3 h-3 inline mr-0.5" />;
  if (val < 0) return <ArrowDown className="w-3 h-3 inline mr-0.5" />;
  return <Minus className="w-3 h-3 inline mr-0.5" />;
}
